import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { PAYMENT_LABELS, FUNDING_LABELS, money, shortDate, programName } from './constants';
import './Screens.css';

// Billing. Only sold seasons appear -- a quote that never became an order is
// pipeline, not a receivable.

const Invoices = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ paymentStatus: '', search: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/crm/invoices?${params.toString()}`);
      setData(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(fetchInvoices, filters.search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchInvoices, filters.search]);

  if (error) return <div className="crm-screen"><p className="banner error">{error}</p></div>;
  if (!data) return <div className="crm-screen"><p className="loading">Loading…</p></div>;

  const s = data.summary || {};
  const rows = data.invoices || [];

  return (
    <div className="crm-screen">
      <div className="screen-header">
        <div>
          <h1>Invoices</h1>
          <p className="subtitle">What's been sold, what's been collected, and what's still out</p>
        </div>
        <span className="season-context">Now selling the {data.year} season</span>
      </div>

      <div className="tiles">
        <div className="tile">
          <div className="tile-value">{money(s.billed)}</div>
          <div className="tile-label">Sold this year</div>
        </div>
        <div className="tile good">
          <div className="tile-value">{money(s.collected)}</div>
          <div className="tile-label">Collected</div>
        </div>
        <div className="tile bad">
          <div className="tile-value">{money(s.outstanding)}</div>
          <div className="tile-label">Outstanding</div>
        </div>
        <div className="tile warn">
          <div className="tile-value">{money(s.unbilled_value)}</div>
          <div className="tile-label">Not yet invoiced · {s.unbilled_count || 0}</div>
        </div>
        <div className="tile bad">
          <div className="tile-value">{s.overdue_count || 0}</div>
          <div className="tile-label">30+ days past due</div>
        </div>
      </div>

      <div className="filters">
        <div className="grow">
          <input
            type="search"
            value={filters.search}
            onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
            placeholder="Search account, invoice, or PO…"
          />
        </div>
        <select
          value={filters.paymentStatus}
          onChange={(e) => setFilters(p => ({ ...p, paymentStatus: e.target.value }))}
        >
          <option value="">All statuses</option>
          {Object.entries(PAYMENT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {loading && rows.length === 0 && <p className="loading">Loading…</p>}

      {!loading && rows.length === 0 ? (
        <div className="card">
          <p className="empty">
            <strong>Nothing billed yet</strong>
            A season shows up here once it's marked ordered or delivered and has an order value on it.
          </p>
        </div>
      ) : (
        <div className="card">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Program</th>
                  <th>Funding</th>
                  <th>PO / Invoice</th>
                  <th className="num">Order</th>
                  <th className="num">Paid</th>
                  <th className="num">Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr
                    key={row.id}
                    className="clickable"
                    onClick={() => navigate(`/crm/organizations/${row.organization_id}`)}
                  >
                    <td>
                      {row.organization_name}
                      {row.payment_terms && <div className="muted" style={{ fontSize: '0.75rem' }}>{row.payment_terms}</div>}
                    </td>
                    <td>{programName(row)}</td>
                    <td className="muted">{FUNDING_LABELS[row.funding_type] || '—'}</td>
                    <td>
                      {row.po_number || row.invoice_number
                        ? <>{row.po_number || '—'}{row.invoice_number ? ` / ${row.invoice_number}` : ''}</>
                        : <span className="muted">—</span>}
                      {row.need_by_date && (
                        <div className="muted" style={{ fontSize: '0.75rem' }}>due {shortDate(row.need_by_date)}</div>
                      )}
                    </td>
                    <td className="num">{money(row.actual_value)}</td>
                    <td className="num">{money(row.amount_paid)}</td>
                    <td className={`num ${Number(row.balance) > 0 ? 'neg' : 'pos'}`}>{money(row.balance)}</td>
                    <td>
                      <span className={`pill pay-${row.payment_status}`}>{PAYMENT_LABELS[row.payment_status]}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
